import { Function1, Predicate, stringValueOrEmpty, styleEm } from "@tevari/helpers";
import { ReactNode, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import Accordion, { IAccordionProps } from "./accordion";
import accordionStyles from "./Accordion.module.scss";
import styles from "./AccordionList.module.scss";

import { createArrayFromOneToN } from "@lib/helper/utils";

export interface AccordionAdditionalItem {
  id: string;
  header: ReactNode;
  content: ReactNode;
  headerClassName?: string;
  contentClassName?: string;
  isDisabled?: boolean;
  className?: string;
}

interface IAccordionListProps<T> {
  items: T[];
  renderHeader: Function1<T, ReactNode>;
  renderContent: Function1<T, ReactNode>;
  isDisabled?: Predicate<T>;
  withAutoClose?: boolean;
  withBorder?: boolean;
  loading?: boolean;
  loaderConfiguration?: { itemSkeleton?: ReactNode; nbOfItems: number; className?: string; itemClassName?: string };
  filter?: Predicate<T>;
  className?: string;
  headerClassName?: string;
  itemClassName?: string;
  contentClassName?: string;
  additionalLeadingItems?: AccordionAdditionalItem[];
  additionalTrailingItems?: AccordionAdditionalItem[];
  itemTestId?: string;
  unopenable?: boolean;
}

const AccordionList = <T,>({
  items,
  renderHeader,
  renderContent,
  isDisabled,
  filter,
  withAutoClose = false,
  loading = false,
  loaderConfiguration = { nbOfItems: 3 },
  className,
  headerClassName,
  itemClassName,
  contentClassName,
  additionalLeadingItems = [],
  additionalTrailingItems = [],
  itemTestId,
  unopenable = false,
  ...props
}: IAccordionListProps<T> & Omit<IAccordionProps, "header" | "children">) => {
  const [openedIndex, setOpenedIndex] = useState<number>();

  const { disabled } = props;

  const _renderDefaultSkeleton = () => (
    <div className={`${accordionStyles["accordion__header--disabled"]} ${accordionStyles["accordion__header"]}`}>
      <Skeleton width="100%" height={styleEm(16)} />
    </div>
  );

  useEffect(() => setOpenedIndex(undefined), [items]);

  const _renderSkeleton = (key: number) => (
    <div
      className={`${accordionStyles["accordion"]} ${accordionStyles["accordion__skeleton"]} ${stringValueOrEmpty(loaderConfiguration.itemClassName)}`}
      key={key}
    >
      {loaderConfiguration.itemSkeleton || _renderDefaultSkeleton()}
    </div>
  );

  if (loading) {
    return (
      <div className={`${styles["accordion-list"]} ${stringValueOrEmpty(loaderConfiguration.className)}`}>
        {createArrayFromOneToN(loaderConfiguration.nbOfItems).map(_renderSkeleton)}
      </div>
    );
  }

  const isLast = (index: number) =>
    index === additionalLeadingItems.length + items.length + additionalTrailingItems.length - 1;

  const _renderAdditionalItems = (position: "leading" | "trailing") => {
    return (position === "leading" ? additionalLeadingItems : additionalTrailingItems).map(
      ({ id, header, content, headerClassName, contentClassName, className, isDisabled }, index) => {
        const realIndex = position === "leading" ? index : additionalLeadingItems.length + index + items.length;
        return (
          <Accordion
            key={id}
            {...props}
            headerClassName={headerClassName}
            contentClassName={contentClassName}
            className={className}
            header={header}
            {...(withAutoClose && {
              onOpen: () => setOpenedIndex(realIndex),
              forceState: openedIndex === realIndex ? "open" : "closed",
            })}
            disabled={isDisabled || disabled}
            isLast={isLast(realIndex)}
          >
            {content}
          </Accordion>
        );
      },
    );
  };

  return (
    <div className={`${styles["accordion-list"]} ${stringValueOrEmpty(className)}`}>
      {_renderAdditionalItems("leading")}
      {items
        .filter((item) => (filter ? filter(item) : true))
        .map((item, index) => {
          const realIndex = additionalLeadingItems.length + index;
          return (
            <Accordion
              key={index}
              {...props}
              headerClassName={headerClassName}
              contentClassName={contentClassName}
              className={itemClassName}
              header={renderHeader(item)}
              {...(withAutoClose && {
                onOpen: () => setOpenedIndex(realIndex),
                forceState: openedIndex === realIndex ? "open" : "closed",
              })}
              disabled={(isDisabled && isDisabled(item)) || disabled}
              isLast={isLast(realIndex)}
              testId={itemTestId}
              unopenable={unopenable}
            >
              {renderContent(item)}
            </Accordion>
          );
        })}
      {_renderAdditionalItems("trailing")}
    </div>
  );
};

export default AccordionList;
